import { BadgeEuro, Briefcase, Building2, CheckCircle2, Loader2, RefreshCcw, Users } from "lucide-react";

import type { ClaimFinancialHealth } from "@upclaim/sdk/src/data-contracts";

import {
  useClaimFinancialHealth,
  useUpdatePaymentCounterparty,
} from "@/features/payments/api/use-traveler-payments";
import {
  type CounterpartyStatus,
  CounterpartyStatusSelect,
} from "@/features/payments/components/counterparty-status-select";
import { getFamilyDisplayName } from "@/features/payments/components/family-payment-breakdown";
import { formatEur } from "@/lib/currency";
import { cn } from "@/lib/utils";

/** Mirrors FinancialHealth.beneficiaryCount (Vue). */
export function getBeneficiaryCount(data: ClaimFinancialHealth | null | undefined): number {
  if (!data) {
    return 0;
  }
  let count = data.passengers.families.length;
  if (data.travelAgency && data.travelAgency.totalCents > 0) {
    count++;
  }
  if (data.travelAgencyCompany && data.travelAgencyCompany.totalCents > 0) {
    count++;
  }
  if (data.upclaim) {
    count++;
  }
  return count;
}

/** Mirrors FinancialHealth.distributionPercentages (Vue). */
export function getDistributionPercentages(data: ClaimFinancialHealth | null | undefined): {
  family: number;
  agency: number;
  enterprise: number;
  upclaim: number;
} {
  if (!data || data.grandTotalDistributedCents === 0) {
    return { family: 0, agency: 0, enterprise: 0, upclaim: 0 };
  }
  const total = data.grandTotalDistributedCents;
  return {
    family: Math.round((data.passengers.totalNetCents / total) * 100),
    agency: Math.round(((data.travelAgency?.totalCents ?? 0) / total) * 100),
    enterprise: Math.round(((data.travelAgencyCompany?.totalCents ?? 0) / total) * 100),
    upclaim: Math.round(((data.upclaim?.totalCents ?? 0) / total) * 100),
  };
}

interface FinancialHealthProps {
  claimId: string;
  claimNumber: number;
}

const EXPENSES_PAYER_LABELS: Record<string, string> = {
  TRAVEL_AGENCY: "frais de l'agence",
  COMPANY: "frais de l'entreprise",
};

/** Port of pages/plugin/FinancialHealth.vue — répartition des montants entre bénéficiaires. */
export function FinancialHealth({ claimId, claimNumber }: FinancialHealthProps) {
  const { data, isPending, isFetching, refetch } = useClaimFinancialHealth(claimNumber);
  const updateCounterparty = useUpdatePaymentCounterparty(claimId);

  const beneficiaryCount = getBeneficiaryCount(data);
  const distributionPercentages = getDistributionPercentages(data);

  function updateStatus(counterpartyId: string, nextStatus: CounterpartyStatus) {
    if (!counterpartyId) {
      return;
    }
    updateCounterparty.mutate({ counterpartyId, status: nextStatus });
  }

  return (
    <div className="min-h-full bg-slate-100">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white px-6 py-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3 text-left">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50">
              <BadgeEuro className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Santé financière</h1>
              <p className="text-sm text-gray-500">
                Répartition des montants entre les bénéficiaires du dossier.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
            disabled={isFetching}
            onClick={() => void refetch()}
            aria-label="Rafraîchir"
          >
            <RefreshCcw className={cn("h-4 w-4", isFetching && "animate-spin")} />
          </button>
        </div>
      </div>

      <div className="p-6">
        {isPending ? (
          <div className="py-12 text-center text-sm text-gray-500">
            <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
            Chargement…
          </div>
        ) : !data ? (
          <div className="rounded-[10px] border border-dashed border-gray-300 bg-white px-6 py-10 text-center text-sm text-gray-500">
            Aucune donnée financière disponible pour ce dossier.
          </div>
        ) : (
          <>
            {/* Summary Card */}
            <div className="mb-6 rounded-[10px] border border-gray-200 bg-white p-5 shadow-sm">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-left">
                  <p className="text-xs text-gray-500">Total reçu</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900 tabular-nums">
                    {formatEur(data.grandTotalReceivedCents)}
                  </p>
                  <p className="text-xs text-gray-400">Encaissé sur le dossier</p>
                </div>
                <div className="text-left">
                  <p className="text-xs text-gray-500">Total distribué</p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900 tabular-nums">
                    {formatEur(data.grandTotalDistributedCents)}
                  </p>
                  <p className="text-xs text-gray-400">Réparti aux bénéficiaires</p>
                </div>
                <div className="flex flex-col items-end text-right">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Solde</span>
                    {data.balanceCents === 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                        <CheckCircle2 className="h-3 w-3" />
                        Équilibré
                      </span>
                    ) : null}
                  </div>
                  <p
                    className={cn(
                      "mt-1 text-2xl font-semibold tabular-nums",
                      data.balanceCents === 0 ? "text-emerald-600" : "text-amber-600",
                    )}
                  >
                    {formatEur(data.balanceCents)}
                  </p>
                  <p className="text-xs text-gray-400">Reçu = distribué</p>
                </div>
              </div>

              {/* Progress bar */}
              <div className="mt-5">
                <div className="flex h-3 w-full overflow-hidden rounded-full bg-gray-100">
                  {distributionPercentages.family > 0 ? (
                    <div className="bg-sky-500" style={{ width: `${distributionPercentages.family}%` }} />
                  ) : null}
                  {distributionPercentages.agency > 0 ? (
                    <div className="bg-amber-400" style={{ width: `${distributionPercentages.agency}%` }} />
                  ) : null}
                  {distributionPercentages.enterprise > 0 ? (
                    <div
                      className="bg-violet-500"
                      style={{ width: `${distributionPercentages.enterprise}%` }}
                    />
                  ) : null}
                  {distributionPercentages.upclaim > 0 ? (
                    <div
                      className="bg-emerald-500"
                      style={{ width: `${distributionPercentages.upclaim}%` }}
                    />
                  ) : null}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                  <div className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-full bg-sky-500" />
                    <span className="text-gray-600">Passager(s)</span>
                    <span className="font-medium text-gray-900 tabular-nums">
                      {formatEur(data.passengers.totalNetCents)}
                    </span>
                  </div>
                  {data.travelAgency ? (
                    <div className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                      <span className="text-gray-600">Agence</span>
                      <span className="font-medium text-gray-900 tabular-nums">
                        {formatEur(data.travelAgency.totalCents)}
                      </span>
                    </div>
                  ) : null}
                  {data.travelAgencyCompany ? (
                    <div className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-violet-500" />
                      <span className="text-gray-600">Entreprise</span>
                      <span className="font-medium text-gray-900 tabular-nums">
                        {formatEur(data.travelAgencyCompany.totalCents)}
                      </span>
                    </div>
                  ) : null}
                  {data.upclaim ? (
                    <div className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                      <span className="text-gray-600">Upclaim</span>
                      <span className="font-medium text-gray-900 tabular-nums">
                        {formatEur(data.upclaim.totalCents)}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Beneficiaries Section Header */}
            <div className="mb-4 flex items-center gap-2">
              <h2 className="text-xs font-semibold tracking-wide text-gray-500 uppercase">Bénéficiaires</h2>
              <span className="text-xs text-gray-400">{beneficiaryCount}</span>
            </div>

            {/* Beneficiary Cards */}
            <div className="space-y-4">
              {/* Passengers — one card per family */}
              {data.passengers.families.map((family) => (
                <div
                  key={family.familyClaimAcquisitionId}
                  className="rounded-[10px] border border-gray-200 bg-white shadow-sm"
                >
                  <div className="flex items-center gap-3 px-5 py-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-50">
                      <Users className="h-5 w-5 text-sky-600" />
                    </div>
                    <div className="min-w-0 flex-1 text-left">
                      <h3 className="text-sm font-semibold text-gray-900">{getFamilyDisplayName(family)}</h3>
                      {family.passengerCount > 1 ? (
                        <p className="text-xs text-gray-500">{family.passengerCount} passagers</p>
                      ) : null}
                    </div>
                    {family.counterpartyId ? (
                      <CounterpartyStatusSelect
                        value={family.status as CounterpartyStatus}
                        disabled={updateCounterparty.isPending}
                        onChange={(status) => updateStatus(family.counterpartyId, status)}
                      />
                    ) : null}
                    <div className="text-right">
                      <p className="text-xs tracking-wide text-gray-400 uppercase">Net</p>
                      <p className="text-lg font-semibold text-emerald-600 tabular-nums">
                        {formatEur(family.netCents)}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2 border-t border-gray-100 px-5 py-3">
                    {family.demands.map((demand, index) => (
                      <div
                        key={`${demand.monetaryDemandId}-${index}`}
                        className="flex justify-between text-sm"
                      >
                        <span className="text-gray-700">{demand.expenseName || demand.displayText}</span>
                        <span className="font-medium text-gray-900 tabular-nums">
                          {formatEur(demand.amountCents)}
                        </span>
                      </div>
                    ))}
                    <div className="flex justify-between text-sm text-gray-400">
                      <span>− Commission Upclaim ({family.commissionRates.upclaimPercentage}%)</span>
                      <span className="tabular-nums">−{formatEur(family.upclaimCommissionCents)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-400">
                      <span>− Commission agence ({family.commissionRates.travelAgencyPercentage}%)</span>
                      <span className="tabular-nums">
                        {family.travelAgencyCommissionCents > 0
                          ? `−${formatEur(family.travelAgencyCommissionCents)}`
                          : formatEur(0)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-gray-100 pt-2">
                      <span className="font-semibold text-gray-900">Net versé au passager</span>
                      <span className="font-semibold text-emerald-600 tabular-nums">
                        {formatEur(family.netCents)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              {/* Travel Agency */}
              {data.travelAgency && data.travelAgency.totalCents > 0 ? (
                <div className="rounded-[10px] border border-gray-200 bg-white shadow-sm">
                  <div className="flex items-center gap-3 px-5 py-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50">
                      <Building2 className="h-5 w-5 text-amber-600" />
                    </div>
                    <div className="min-w-0 flex-1 text-left">
                      <h3 className="text-sm font-semibold text-gray-900">Agence de voyage</h3>
                      <p className="text-xs text-gray-500">
                        Apporteur — {data.travelAgency.companyName || "N/A"}
                      </p>
                    </div>
                    {data.travelAgency.counterpartyId ? (
                      <CounterpartyStatusSelect
                        value={data.travelAgency.status as CounterpartyStatus}
                        disabled={updateCounterparty.isPending}
                        onChange={(status) => updateStatus(data.travelAgency.counterpartyId, status)}
                      />
                    ) : null}
                    <div className="text-right">
                      <p className="text-xs tracking-wide text-gray-400 uppercase">Net</p>
                      <p className="text-lg font-semibold text-emerald-600 tabular-nums">
                        {formatEur(data.travelAgency.totalCents)}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2 border-t border-gray-100 px-5 py-3">
                    {data.travelAgency.expenses.map((expense, index) => (
                      <div key={`${expense.type}-${index}`} className="flex justify-between text-sm">
                        <span className="text-gray-700">{expense.name || expense.type}</span>
                        <span className="font-medium text-gray-900 tabular-nums">
                          {formatEur(expense.amountCents)}
                        </span>
                      </div>
                    ))}
                    {data.travelAgency.expensesBreakdown.upclaimCommissionCents ? (
                      <div className="flex justify-between text-sm text-gray-400">
                        <span>
                          − Commission Upclaim (
                          {data.travelAgency.expensesBreakdown.commissionRates.upclaimPercentage}%)
                        </span>
                        <span className="tabular-nums">
                          −{formatEur(data.travelAgency.expensesBreakdown.upclaimCommissionCents)}
                        </span>
                      </div>
                    ) : null}
                    {data.travelAgency.commissionCents ? (
                      <div className="flex justify-between text-sm text-gray-400">
                        <span>+ Commission passagers ({data.travelAgency.commissionPercentage}%)</span>
                        <span className="tabular-nums">+{formatEur(data.travelAgency.commissionCents)}</span>
                      </div>
                    ) : null}
                    {data.travelAgency.expensesCommissions.map((line) => (
                      <div key={line.payer} className="flex justify-between text-sm text-gray-400">
                        <span>
                          + Commission {EXPENSES_PAYER_LABELS[line.payer]} ({line.percentage}%)
                        </span>
                        <span className="tabular-nums">+{formatEur(line.amountCents)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {/* Enterprise (business travel company) */}
              {data.travelAgencyCompany && data.travelAgencyCompany.totalCents > 0 ? (
                <div className="rounded-[10px] border border-gray-200 bg-white shadow-sm">
                  <div className="flex items-center gap-3 px-5 py-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-violet-50">
                      <Briefcase className="h-5 w-5 text-violet-600" />
                    </div>
                    <div className="min-w-0 flex-1 text-left">
                      <h3 className="text-sm font-semibold text-gray-900">Entreprise</h3>
                      <p className="text-xs text-gray-500">
                        Frais avancés — {data.travelAgencyCompany.companyName || "N/A"}
                      </p>
                    </div>
                    {data.travelAgencyCompany.counterpartyId ? (
                      <CounterpartyStatusSelect
                        value={data.travelAgencyCompany.status as CounterpartyStatus}
                        disabled={updateCounterparty.isPending}
                        onChange={(status) => updateStatus(data.travelAgencyCompany.counterpartyId, status)}
                      />
                    ) : null}
                    <div className="text-right">
                      <p className="text-xs tracking-wide text-gray-400 uppercase">Net</p>
                      <p className="text-lg font-semibold text-emerald-600 tabular-nums">
                        {formatEur(data.travelAgencyCompany.totalCents)}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2 border-t border-gray-100 px-5 py-3">
                    {data.travelAgencyCompany.expenses.map((expense, index) => (
                      <div key={`${expense.type}-${index}`} className="flex justify-between text-sm">
                        <span className="text-gray-700">{expense.name || expense.type}</span>
                        <span className="font-medium text-gray-900 tabular-nums">
                          {formatEur(expense.amountCents)}
                        </span>
                      </div>
                    ))}
                    {data.travelAgencyCompany.expensesBreakdown.upclaimCommissionCents ? (
                      <div className="flex justify-between text-sm text-gray-400">
                        <span>
                          − Commission Upclaim (
                          {data.travelAgencyCompany.expensesBreakdown.commissionRates.upclaimPercentage}%)
                        </span>
                        <span className="tabular-nums">
                          −{formatEur(data.travelAgencyCompany.expensesBreakdown.upclaimCommissionCents)}
                        </span>
                      </div>
                    ) : null}
                    {data.travelAgencyCompany.expensesBreakdown.travelAgencyCommissionCents ? (
                      <div className="flex justify-between text-sm text-gray-400">
                        <span>
                          − Commission agence (
                          {data.travelAgencyCompany.expensesBreakdown.commissionRates.travelAgencyPercentage}
                          %)
                        </span>
                        <span className="tabular-nums">
                          −{formatEur(data.travelAgencyCompany.expensesBreakdown.travelAgencyCommissionCents)}
                        </span>
                      </div>
                    ) : null}
                    <div className="flex justify-between border-t border-gray-100 pt-2">
                      <span className="font-semibold text-gray-900">Net versé à l'entreprise</span>
                      <span className="font-semibold text-emerald-600 tabular-nums">
                        {formatEur(data.travelAgencyCompany.totalCents)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Upclaim */}
              {data.upclaim ? (
                <div className="rounded-[10px] border border-gray-200 bg-white shadow-sm">
                  <div className="flex items-center gap-3 px-5 py-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50">
                      <BadgeEuro className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div className="min-w-0 flex-1 text-left">
                      <h3 className="text-sm font-semibold text-gray-900">Upclaim</h3>
                      <p className="text-xs text-gray-500">Honoraires de gestion</p>
                    </div>
                    {data.upclaim.counterpartyId ? (
                      <CounterpartyStatusSelect
                        value={data.upclaim.status as CounterpartyStatus}
                        disabled={updateCounterparty.isPending}
                        onChange={(status) => updateStatus(data.upclaim.counterpartyId, status)}
                      />
                    ) : null}
                    <div className="text-right">
                      <p className="text-xs tracking-wide text-gray-400 uppercase">Marge</p>
                      <p className="text-lg font-semibold text-emerald-600 tabular-nums">
                        {formatEur(data.upclaim.totalCents)}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2 border-t border-gray-100 px-5 py-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">
                        Commission passagers ({data.upclaim.commissionPercentage}%)
                      </span>
                      <span className="font-medium text-gray-900 tabular-nums">
                        {formatEur(data.upclaim.commissionCents)}
                      </span>
                    </div>
                    {data.upclaim.expensesCommissions.map((line) => (
                      <div key={line.payer} className="flex justify-between text-sm">
                        <span className="text-gray-700">
                          Commission {EXPENSES_PAYER_LABELS[line.payer]} ({line.percentage}%)
                        </span>
                        <span className="font-medium text-gray-900 tabular-nums">
                          {formatEur(line.amountCents)}
                        </span>
                      </div>
                    ))}
                    {data.upclaim.notDistributedDemands.map((demand) => (
                      <div key={demand.type} className="flex justify-between text-sm">
                        <span className="text-gray-700">{demand.displayText}</span>
                        <span className="font-medium text-gray-900 tabular-nums">
                          {formatEur(demand.receivedAmountCents)}
                        </span>
                      </div>
                    ))}
                    <div className="flex justify-between border-t border-gray-100 pt-2">
                      <span className="font-semibold text-gray-900">Marge Upclaim</span>
                      <span className="font-semibold text-emerald-600 tabular-nums">
                        {formatEur(data.upclaim.totalCents)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
